import type { CartValidationErrorDetails, ItemsListItem, RefetchCartStateOptions } from '@libs/common-next';
import {
    buildItemsListFromCart,
    CartValidationError,
    CartValidationErrorKey,
    reapplyNonDiscontinuedCartValidationErrors,
    runDiscontinuedItemsRemovedRecovery,
} from '@libs/common-next';
import type { CartUI, CartValidationErrorResponse } from '@libs/common-node';
import type { CartNotification } from '@libs/common-types';
import { HttpStatus } from '@libs/core';

export type RecoverDiscontinuedFromCartMergeValidationArgs = {
    validationResponse: CartValidationErrorResponse;
    mergedCart?: CartUI | null;
    handleCartError: (error: unknown, itemsOverride?: ItemsListItem[]) => CartValidationErrorDetails[];
    setNotifications: (updater: (prev: CartNotification[] | undefined) => CartNotification[]) => void;
    removeLineItemsFromCartState: (lineItemIds: string[]) => void;
    refetchCart: (options?: RefetchCartStateOptions) => Promise<CartUI | null>;
    clearStockErrors: () => void;
    clearErrors: () => void;
};

export const isCartMergeValidationFailureResponse = (result: unknown): result is CartValidationErrorResponse =>
    Boolean(
        result &&
            typeof result === 'object' &&
            'status' in result &&
            (result as CartValidationErrorResponse).status === HttpStatus.UNPROCESSABLE_ENTITY
    );

export async function recoverDiscontinuedItemsFromCartMergeValidation({
    validationResponse,
    mergedCart,
    handleCartError,
    setNotifications,
    removeLineItemsFromCartState,
    refetchCart,
    clearStockErrors,
    clearErrors,
}: RecoverDiscontinuedFromCartMergeValidationArgs): Promise<boolean> {
    const validationError = new CartValidationError(
        validationResponse.title ?? 'Cart validation failed',
        validationResponse
    );
    const itemsForParsing = mergedCart ? buildItemsListFromCart(mergedCart) : undefined;
    const parsedErrors = handleCartError(validationError, itemsForParsing);
    const hasDiscontinued = parsedErrors.some(error => error.errorCode === CartValidationErrorKey.DISCONTINUED_PRODUCT);

    if (!hasDiscontinued) {
        return false;
    }

    let cartAfterDiscontinuedRecovery: CartUI | null | undefined;

    const refetchOptions: RefetchCartStateOptions = {
        preserveUpdatingState: true,
        mergePersistedNotifications: true,
        ...(mergedCart?.id ? { cartId: mergedCart.id, previousCart: mergedCart } : {}),
    };

    await runDiscontinuedItemsRemovedRecovery({
        validationErrors: parsedErrors,
        setNotifications,
        removeLineItemsFromCartState,
        refetchCart: async () => {
            const refetchedCart = await refetchCart(refetchOptions);
            if (refetchedCart && typeof refetchedCart === 'object' && 'id' in refetchedCart) {
                cartAfterDiscontinuedRecovery = refetchedCart as CartUI;
            }
        },
        clearStockErrors,
        clearErrors,
        whenRefetchFails: 'continue',
    });

    reapplyNonDiscontinuedCartValidationErrors({
        parsedValidationErrors: parsedErrors,
        validationResponse,
        cartAfterRecovery: cartAfterDiscontinuedRecovery,
        handleCartError,
    });

    return true;
}
